import { useCallback, useEffect, useRef, useState } from "react";
import di from "../../di/di";
import { useAuth } from "./useAuth";
import { useRouter } from "./useRouter";
import { useSessionAnalytics } from "./useSessionAnalytics";
import { isRight } from "../types/Either";
import { SetSessionEndedReasonParams } from "../../domain/useCase/SetSessionEndedReason";
import {
  SESSION_ACTIVITY_POLL_INTERVAL_MS,
  SESSION_ACTIVITY_WRITE_THROTTLE_MS,
  SESSION_ENDED_REASON_IDLE,
  SESSION_IDLE_TIMEOUT_MINUTES,
  SESSION_IDLE_WARNING_SECONDS,
  SESSION_RENEWAL_CHECK_INTERVAL_MS,
  SESSION_RENEWAL_WINDOW_SECONDS,
} from "../constants/environment";

/**
 * Real user-input events only. Background XHR — this hook's own poll
 * included — must never count as activity, or the poll would keep every
 * session alive forever regardless of whether anyone is at the keyboard.
 *
 * `mousemove` and `touchmove` are included deliberately. A member reading a
 * long plan document or filling a slow form may go minutes without clicking,
 * typing or scrolling while plainly still at the screen, and PPI-1848's
 * requirement is that an active member is never signed out. Pointer movement
 * is the signal that distinguishes "reading" from "walked away".
 *
 * The tradeoff, accepted: a nudged desk or a pointer resting under something
 * that moves can keep a session alive. That is the same tradeoff every
 * mainstream idle-timer makes, and the alternative — signing out someone who
 * is demonstrably present — is what gets these controls switched off
 * altogether (see PPI-1873 on why a painful timeout does not survive).
 *
 * Cost is bounded by SESSION_ACTIVITY_WRITE_THROTTLE_MS: a continuous stream
 * of mousemove events still writes the cookie at most once every 5 seconds.
 */
const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "touchmove",
];

export interface SessionActivityState {
  isWarningVisible: boolean;
  secondsRemaining: number;
  extendSession: () => void;
}

/**
 * Drives the member portal's HITRUST idle-session control (PPI-1848):
 *  - signs the member out after SESSION_IDLE_TIMEOUT_MINUTES of no real input,
 *  - warns them SESSION_IDLE_WARNING_SECONDS before that happens,
 *  - and, independently of idle state, renews the token so the auth service's
 *    sliding-window rotation keeps an active member signed in indefinitely.
 *
 * Idle state is always re-derived by comparing two timestamps on a short
 * poll, never armed as a single long setTimeout. A setTimeout scheduled 15
 * minutes out fires 15 minutes of *wall-clock* time after it was armed — it
 * does not fire while the laptop is asleep and does not know how long the
 * machine was suspended. Re-deriving from timestamps means a laptop that
 * wakes 40 minutes later is correctly treated as already-idle on the very
 * next tick.
 *
 * `visibilitychange` therefore forces an immediate RE-CHECK, never an
 * activity write. Recording activity there would be precisely backwards:
 * returning to a tab backgrounded for hours would reset the idle clock and
 * the member would never be signed out — the exact finding this control
 * exists to close. It is also why the listener is gated on
 * `!document.hidden`; the same event fires when the tab is being hidden,
 * which is someone leaving, not arriving.
 *
 * Last-activity lives in a cookie, reached through a use case rather than
 * touched here, so activity in one tab resets the idle clock every other tab
 * of the same origin reads.
 *
 * Cross-tab tradeoff, accepted deliberately: the renewal-check interval is
 * per-tab, not shared. Two tabs open on the same near-expiry session can each
 * poke the auth service once per interval — at most one redundant call per
 * open tab, never a correctness issue, since the server simply rotates again.
 */
export function useSessionActivity(): SessionActivityState {
  const auth = useAuth();
  const router = useRouter();
  const analytics = useSessionAnalytics();
  const [isWarningVisible, setWarningVisible] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const lastActivityWriteAtRef = useRef(0);
  const lastRenewalCheckAtRef = useRef(0);
  const hasEndedRef = useRef(false);
  const isTickingRef = useRef(false);
  // The warning is re-evaluated on every tick, but it is one event per
  // occurrence, not one per five seconds.
  const hasWarnedRef = useRef(false);

  const recordActivity = useCallback((): void => {
    const now = Date.now();
    if (
      now - lastActivityWriteAtRef.current <
      SESSION_ACTIVITY_WRITE_THROTTLE_MS
    ) {
      return;
    }
    lastActivityWriteAtRef.current = now;
    di.touchSessionActivity.execute();
  }, []);

  // Re-entry is prevented by the caller: `tick` checks hasEndedRef before it
  // does anything, and it is the only caller. The flag is still set here so
  // that check has something to read.
  const endSessionForIdle = useCallback((): void => {
    hasEndedRef.current = true;
    setWarningVisible(false);
    analytics.trackIdleLogout({
      idleWindowMinutes: SESSION_IDLE_TIMEOUT_MINUTES,
    });
    // The reason is written before signing out, so /login can explain what
    // happened. auth.logout() clears the activity clock but deliberately not
    // this value.
    di.setSessionEndedReason
      .execute(new SetSessionEndedReasonParams(SESSION_ENDED_REASON_IDLE))
      .then(() => {
        auth.logout();
        router.replace("/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, router]);

  const extendSession = useCallback((): void => {
    lastActivityWriteAtRef.current = 0; // force a write, bypassing the throttle
    recordActivity();
    setWarningVisible(false);
    hasWarnedRef.current = false;
    analytics.trackSessionExtended({
      idleWindowMinutes: SESSION_IDLE_TIMEOUT_MINUTES,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordActivity]);

  useEffect(() => {
    if (!auth.user) {
      return undefined;
    }

    hasEndedRef.current = false;
    recordActivity();

    const idleLimitMs = SESSION_IDLE_TIMEOUT_MINUTES * 60 * 1000;
    const warningThresholdMs =
      idleLimitMs - SESSION_IDLE_WARNING_SECONDS * 1000;

    const tick = async (): Promise<void> => {
      // The poll interval is shorter than a slow round-trip, and
      // visibilitychange can fire a tick mid-interval, so guard against two
      // ticks interleaving and double-firing a renewal.
      if (hasEndedRef.current || isTickingRef.current) {
        return;
      }
      isTickingRef.current = true;

      try {
        const idle = await di.getSessionIdleMilliseconds.execute();
        if (!isRight(idle)) {
          // A read that actually threw is unexpected and assumed transient;
          // skip this tick rather than signing someone out on a one-off
          // failure. A *missing* cookie is not this branch — the repository
          // reports that as fully idle, which does end the session.
          return;
        }

        const idleMs = idle.value;

        if (idleMs >= idleLimitMs) {
          endSessionForIdle();
          return;
        }

        if (idleMs >= warningThresholdMs) {
          if (!hasWarnedRef.current) {
            hasWarnedRef.current = true;
            analytics.trackIdleWarningShown({
              idleWindowMinutes: SESSION_IDLE_TIMEOUT_MINUTES,
            });
          }
          setWarningVisible(true);
          setSecondsRemaining(Math.ceil((idleLimitMs - idleMs) / 1000));
        } else {
          hasWarnedRef.current = false;
          setWarningVisible(false);
        }

        const now = Date.now();
        if (
          now - lastRenewalCheckAtRef.current <
          SESSION_RENEWAL_CHECK_INTERVAL_MS
        ) {
          return;
        }
        // Stamped on every check, not only when a renewal fires. Stamping
        // only on fire would leave the check un-throttled for the ~99% of a
        // session where the token is nowhere near expiry, decoding the JWT
        // on every 5-second poll.
        lastRenewalCheckAtRef.current = now;

        const remaining = await di.getTokenRemainingSeconds.execute();
        if (
          isRight(remaining) &&
          remaining.value > 0 &&
          remaining.value <= SESSION_RENEWAL_WINDOW_SECONDS
        ) {
          // Deliberately NOT auth.getUser(): that chain calls
          // getApplication(), which flips gettingApplication and would flash
          // the dashboard's loading skeletons and re-fire the userSetted /
          // applicationSetted GTM events at an active member on every
          // renewal. RenewSession persists the rotated token and touches no
          // React state at all.
          //
          // The outcome is reported either way. A rising renewal-failure
          // rate is the only early signal that active members are about to
          // start being signed out mid-task; there is no way to see it from
          // outside until support tickets arrive.
          const remainingAtCheck = remaining.value;
          di.renewSession.execute().then((renewal) => {
            if (isRight(renewal)) {
              analytics.trackTokenRenewed({
                secondsRemaining: remainingAtCheck,
              });
            } else {
              analytics.trackTokenRenewalFailed({
                secondsRemaining: remainingAtCheck,
              });
            }
          });
        }
      } finally {
        isTickingRef.current = false;
      }
    };

    const onVisibilityChange = (): void => {
      if (!document.hidden) {
        tick();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, recordActivity, { passive: true })
    );
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = window.setInterval(
      tick,
      SESSION_ACTIVITY_POLL_INTERVAL_MS
    );
    tick();

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, recordActivity)
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
      // Force the warning closed the instant auth.user goes null for any
      // reason — idle logout, header logout, or the token dying server-side.
      // Without this, a warning left open as the member is redirected would
      // render as a ghost modal on top of the login page.
      setWarningVisible(false);
      setSecondsRemaining(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user]);

  return { isWarningVisible, secondsRemaining, extendSession };
}
